import { AppWindow } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDuration } from "../../utils";
import { buildPooledAppRows } from "./processAppUsage";
import type { ProcessNodeOccurrence } from "./processGraphModel";
import { CollapsibleSidebarSection, HelpHint } from "./processSidebarUi";

export default function ProcessStepAppUsage({
  occurrences,
}: { occurrences: ProcessNodeOccurrence[] }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const rows = useMemo(
    () =>
      buildPooledAppRows(occurrences, t("processPanel.stepOtherApps"), Number.POSITIVE_INFINITY),
    [occurrences, t],
  );
  if (rows.length === 0) return null;
  return (
    <CollapsibleSidebarSection
      title={t("app_usage")}
      headerIcon={<AppWindow className="h-4 w-4" aria-hidden="true" />}
      summary={t("processPanel.stepAppsSummary", {
        app: rows[0].name,
        pct: Math.round(rows[0].pct),
      })}
      defaultOpen={false}
      separated={false}
      className="border-t border-outline pt-4"
    >
      <div className="w-full overflow-hidden rounded-lg border border-outline">
        <table className="w-full table-fixed text-sm" aria-label={t("app_usage")}>
          <colgroup>
            <col />
            <col className="w-24" />
            <col className="w-20" />
          </colgroup>
          <thead className="border-b border-outline bg-surface-strong/60 text-xs text-text-dim">
            <tr>
              <th scope="col" className="px-3 py-2.5 text-left font-medium">
                {t("processPanel.stepApp")}
              </th>
              <th scope="col" className="px-3 py-2.5 text-right font-medium">
                {t("processPanel.stepAppTime")}
              </th>
              <th scope="col" className="px-3 py-2.5 text-right font-medium">
                <span className="inline-flex items-center gap-1.5">
                  {t("processPanel.stepAppShare")}
                  <HelpHint
                    standalone
                    label={t("processPanel.stepAppShare")}
                    text={t("processPanel.stepOtherAppsHelp")}
                    align="end"
                  />
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline">
            {(expanded ? rows : rows.slice(0, 3)).map(row => (
              <tr key={row.name} className="even:bg-surface-strong/20">
                <th scope="row" className="px-3 py-3 text-left font-normal">
                  <span className="flex min-w-0 items-center gap-4">
                    <span className="flex w-44 max-w-[60%] min-w-0 shrink-0 items-center gap-2">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                        {row.iconUri ? (
                          <img src={row.iconUri} alt="" className="h-5 w-5 object-contain" />
                        ) : (
                          <span className="h-3 w-3 rounded-sm" style={{ background: row.color }} />
                        )}
                      </span>
                      <span
                        title={row.name}
                        className={`truncate ${row.isOther ? "text-text-mid" : "text-text"}`}
                      >
                        {row.name}
                      </span>
                    </span>
                    <span
                      aria-hidden="true"
                      className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-app-usage-track"
                    >
                      <span
                        className={`block h-full rounded-full ${row.isOther ? "bg-app-usage-other" : "bg-app-usage/70"}`}
                        style={{ width: `${Math.min(100, row.pct)}%` }}
                      />
                    </span>
                  </span>
                </th>
                <td className="px-3 py-3 text-right text-xs tabular-nums text-text-mid">
                  {formatDuration(row.seconds)}
                </td>
                <td className="px-3 py-3 text-right text-xs font-medium tabular-nums text-text">
                  {Math.round(row.pct)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 3 && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded(value => !value)}
          className="mt-2 w-full rounded-md py-2 text-xs font-medium text-info hover:bg-surface-strong focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          {t(expanded ? "show_less" : "processPanel.stepAllApps", { count: rows.length })}
        </button>
      )}
    </CollapsibleSidebarSection>
  );
}
